import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Logo } from './Logo';
import { SparklesIcon, FileTextIcon, BookmarkIcon, MessageSquareIcon, UserIcon, BriefcaseIcon, ShoppingBagIcon, LifeBuoyIcon, LockIcon } from 'lucide-react';
import { useBusiness } from './BusinessContext';
import { useAuth } from './AuthContext';
type NavItem = {
  to: string;
  label: string;
  icon: React.ElementType;
  badge?: boolean;
};
export function Sidebar() {
  const {
    hub
  } = useBusiness();
  const {
    isAuthenticated,
    openLogin,
    requireAuth
  } = useAuth();
  const navigate = useNavigate();
  const navItems: NavItem[] = [{
    to: '/',
    label: 'Discover',
    icon: SparklesIcon
  }, {
    to: '/requests',
    label: 'Requests',
    icon: FileTextIcon
  }, {
    to: '/saved',
    label: 'Saved',
    icon: BookmarkIcon
  }, {
    to: '/messages',
    label: 'Messages',
    icon: MessageSquareIcon
  },
  // Business Hub is always available to signed-in users; My Shop appears
  // once the hub has been activated.
  {
    to: '/business',
    label: 'Business Hub',
    icon: BriefcaseIcon,
    badge: hub?.isNew
  } as NavItem, ...(hub?.active ? [{
    to: `/shop/${hub.shopSlug || 'shop'}`,
    label: 'My Shop',
    icon: ShoppingBagIcon
  } as NavItem] : []), {
    to: '/profile',
    label: 'Profile',
    icon: UserIcon
  }];
  return <aside className="app-fill-screen hidden lg:flex lg:flex-col w-64 bg-white border-r border-slate-100 h-screen sticky top-0" data-id="element-2125">
      <div className="p-6" data-id="element-2126">
        <Logo size="md" data-id="element-2127" />
      </div>

      <nav className="flex-1 px-4 space-y-1 overflow-y-auto" data-id="element-2128">
        {navItems.map(item => {
        const locked = !isAuthenticated && item.to !== '/';
        if (locked) {
          return <button key={item.to} onClick={() => requireAuth(() => navigate(item.to))} title="Sign in to access" className="group relative w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-50 transition-all" data-id="element-2129">
                <item.icon className="w-5 h-5" data-id="element-2130" />
                <span className="flex-1 text-left" data-id="element-2131">{item.label}</span>
                <LockIcon className="w-4 h-4 text-slate-300 group-hover:text-slate-400 transition-colors" data-id="element-2132" />
              </button>;
        }
        return <NavLink key={item.to} to={item.to} onClick={item.to === '/' ? e => {
          // Clicking Discover always starts a fresh chat,
          // even when already on the Discover route.
          e.preventDefault();
          navigate('/', {
            state: {
              reset: Date.now()
            }
          });
        } : undefined} className={({
          isActive
        }) => `relative flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${item.badge ? 'bg-gradient-to-r from-primary-indigo/10 to-ai-purple/10 text-primary-indigo font-medium ring-1 ring-primary-indigo/30 animate-pulse' : isActive ? 'bg-primary-indigo/5 text-primary-indigo font-medium' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-2133">
              {({
            isActive
          }) => <>
                  <item.icon className={`w-5 h-5 ${isActive || item.badge ? 'text-primary-indigo' : ''}`} data-id="element-2134" />
                  <span className="flex-1" data-id="element-2135">{item.label}</span>
                  {item.badge && <span className="text-[10px] font-bold uppercase tracking-wide bg-gradient-to-r from-primary-indigo to-ai-purple text-white px-2 py-0.5 rounded-full shadow-sm shadow-primary-indigo/40" data-id="element-2136">
                      New
                    </span>}
                </>}
            </NavLink>;
      })}
      </nav>

      {/* Help — pinned to the bottom */}
      <div className="px-4 pb-4 pt-2 border-t border-slate-100 mt-2" data-id="element-2137">
        {!isAuthenticated ? <button onClick={openLogin} title="Sign in to access" className="group relative w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-50 transition-all" data-id="element-2138">
            <LifeBuoyIcon className="w-5 h-5" data-id="element-2139" />
            <span className="flex-1 text-left" data-id="element-2140">Help</span>
            <LockIcon className="w-4 h-4 text-slate-300 group-hover:text-slate-400 transition-colors" data-id="element-2141" />
          </button> : <NavLink to="/help" className={({
        isActive
      }) => `relative flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${isActive ? 'bg-primary-indigo/5 text-primary-indigo font-medium' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-2142">
            {({
          isActive
        }) => <>
                <LifeBuoyIcon className={`w-5 h-5 ${isActive ? 'text-primary-indigo' : ''}`} data-id="element-2143" />
                <span className="flex-1" data-id="element-2144">Help</span>
              </>}
          </NavLink>}
      </div>
    </aside>;
}